/**
 * Actions By Type Chart Component
 * Gráfico de barras mostrando distribuição de ações por tipo
 */

import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, Alert } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface ActionsByTypeChartProps {
  data: Array<{
    action: string;
    count: number;
    successRate: number;
  }>;
  loading?: boolean;
  error?: string | null;
}

export const ActionsByTypeChart: React.FC<ActionsByTypeChartProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const colors = [
    'rgba(102, 126, 234, 0.9)',
    'rgba(118, 75, 162, 0.9)',
    'rgba(255, 159, 64, 0.9)',
    'rgba(75, 192, 192, 0.9)',
    'rgba(153, 102, 255, 0.9)',
    'rgba(255, 99, 132, 0.9)',
    'rgba(199, 199, 199, 0.9)',
    'rgba(54, 162, 235, 0.9)',
    'rgba(255, 206, 86, 0.9)',
    'rgba(99, 255, 132, 0.9)'
  ];

  const chartData = {
    labels: data.map(item => item.action),
    datasets: [
      {
        label: 'Quantidade de ações',
        data: data.map(item => item.count),
        backgroundColor: colors.slice(0, data.length),
        borderColor: colors.slice(0, data.length).map(c => c.replace('0.9', '1')),
        borderWidth: 1
      }
    ]
  };

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          afterLabel: function (context: any) {
            const item = data[context.dataIndex];
            return item ? `Taxa de sucesso: ${item.successRate}%` : '';
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Quantidade'
        }
      },
      y: {
        ticks: {
          maxRotation: 0,
          autoSkip: false
        }
      }
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Distribuição de Ações por Tipo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Identifique quais tipos de ações são mais frequentes e suas taxas de sucesso
        </Typography>
        <Box sx={{ height: Math.max(320, data.length * 44), minHeight: 320 }}>
          <Bar data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};
